class AeProductCard extends HTMLElement {
  /** @type {number} */
  #imgIndex = 0;

  /** @type {HTMLElement | null} */
  #imgTrack = null;

  /** @type {NodeListOf<HTMLElement> | null} */
  #imgSlides = null;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {AbortController | undefined} */
  #listeners;

  connectedCallback() {
    this.#listeners = new AbortController();
    this.#initImages();
    this.#initSizeDrawer();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#listeners?.abort();
  }

  #initImages() {
    this.#imgTrack = this.querySelector('.ae-card__img-track');
    this.#imgSlides = this.querySelectorAll('.ae-card__img-slide');
    if (!this.#imgTrack || this.#imgSlides.length <= 1) return;

    const signal = this.#listeners?.signal;
    this.querySelector('.ae-card__img-btn--prev')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.#goToImage(this.#imgIndex - 1);
    }, { signal });

    this.querySelector('.ae-card__img-btn--next')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.#goToImage(this.#imgIndex + 1);
    }, { signal });
  }

  /** @param {number} index */
  #goToImage(index) {
    if (!this.#imgTrack || !this.#imgSlides) return;
    const max = this.#imgSlides.length - 1;
    this.#imgIndex = Math.min(Math.max(0, index), max);
    this.#imgTrack.style.transform = `translateX(${-this.#imgIndex * 100}%)`;
  }

  #initSizeDrawer() {
    const drawer = /** @type {HTMLElement | null} */ (this.querySelector('.ae-card__size-drawer'));
    if (!drawer) return;

    const signal = this.#listeners?.signal;

    for (const openBtn of this.querySelectorAll('.ae-card__quick-open')) {
      openBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.#openDrawer(drawer);
      }, { signal });
    }

    drawer.querySelector('.ae-card__size-close')?.addEventListener('click', (e) => {
      e.preventDefault();
      this.#closeDrawer(drawer);
    }, { signal });

    for (const sizeBtn of drawer.querySelectorAll('.ae-card__size-btn')) {
      sizeBtn.addEventListener('click', () => {
        const variantId = /** @type {HTMLElement} */ (sizeBtn).dataset.variantId;
        if (variantId) this.#addToCart(variantId, drawer);
      }, { signal });
    }

    document.addEventListener('click', (e) => {
      if (!this.contains(/** @type {Node} */ (e.target)) && !drawer.hidden) {
        this.#closeDrawer(drawer);
      }
    }, { signal });
  }

  /** @param {HTMLElement} drawer */
  #openDrawer(drawer) {
    drawer.hidden = false;
    // Defer so CSS transition fires
    requestAnimationFrame(() => {
      requestAnimationFrame(() => drawer.removeAttribute('hidden'));
    });
  }

  /** @param {HTMLElement} drawer */
  #closeDrawer(drawer) {
    drawer.hidden = true;
  }

  /**
   * @param {string} variantId
   * @param {HTMLElement} drawer
   */
  async #addToCart(variantId, drawer) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    this.setAttribute('data-adding', '');

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: 1 }),
        signal: this.#abortController.signal,
      });

      if (!res.ok) throw new Error('Cart add failed');

      const data = await res.json();

      // Notify Horizon's cart system (ThemeEvents.cartUpdate = 'cart:update')
      // Use 'product-form-component' source so cart-icon sums itemCount to current count
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: {},
          sourceId: 'ae-product-card',
          data: {
            itemCount: 1,
            source: 'product-form-component',
          }
        }
      }));

      // Show toast notification
      document.dispatchEvent(new CustomEvent('cart:toast', {
        bubbles: true,
        detail: {
          title: data.product_title,
          variant: data.variant_title,
        }
      }));

      this.#closeDrawer(drawer);
    } catch (err) {
      if (/** @type {Error} */ (err).name !== 'AbortError') {
        console.error('[ae-product-card] Add to cart failed:', err);
      }
    } finally {
      this.removeAttribute('data-adding');
    }
  }
}

if (!customElements.get('ae-product-card')) {
  customElements.define('ae-product-card', AeProductCard);
}

// ============================================================================
// Color Siblings Service - Ported from shopify-horizon
// ============================================================================
(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Cache + Deduplication (global, not per-instance)
  // ---------------------------------------------------------------------------
  const CACHE_PREFIX = 'siblings_v6_';
  const MAX_CACHE_ENTRIES = 50;
  /** @type {string[]} */
  const cachedKeys = [];
  /** @type {Object.<string, Promise>} */
  const pendingRequests = {};

  function cacheKey(styleRef) {
    return CACHE_PREFIX + styleRef;
  }

  function getFromCache(styleRef) {
    try {
      const raw = sessionStorage.getItem(cacheKey(styleRef));
      if (raw) return JSON.parse(raw);
    } catch (e) {}
    return null;
  }

  function saveToCache(styleRef, products) {
    try {
      // Prune first to make room
      while (cachedKeys.length >= MAX_CACHE_ENTRIES) {
        sessionStorage.removeItem(cachedKeys.shift());
      }
      sessionStorage.setItem(cacheKey(styleRef), JSON.stringify(products));
      cachedKeys.push(cacheKey(styleRef));
    } catch (e) {}
  }

  // ---------------------------------------------------------------------------
  // Filter helpers
  // ---------------------------------------------------------------------------

  /**
   * @param {Array<{tags?: string[], handle: string}>} products
   * @param {string} styleRef
   * @returns {Array}
   */
  function filterSiblings(products, styleRef) {
    const erpPrefix = styleRef + '-';
    const styleRefLower = styleRef.toLowerCase();
    return products.filter((p) => {
      if (p.tags && p.tags.length) {
        return p.tags.some((t) => t.indexOf(erpPrefix) === 0);
      }
      return p.handle && p.handle.toLowerCase().indexOf(styleRefLower) !== -1;
    });
  }

  // ---------------------------------------------------------------------------
  // Storefront API
  // ---------------------------------------------------------------------------
  const USE_STOREFRONT_API = !!(
    window.STOREFRONT_ACCESS_TOKEN &&
    window.STOREFRONT_ACCESS_TOKEN.length > 10 &&
    window.STOREFRONT_DOMAIN
  );

  /**
   * @param {Object} node
   * @returns {Object}
   */
  function normalizeFromStorefront(node) {
    let colorHex = null;
    let colorName = null;

    if (node.options) {
      for (let i = 0; i < node.options.length; i++) {
        const opt = node.options[i];
        const nameLower = opt.name.toLowerCase();
        if (nameLower === 'color' || nameLower === 'colour') {
          if (opt.optionValues && opt.optionValues.length > 0) {
            colorName = opt.optionValues[0].name;
            if (opt.optionValues[0].swatch && opt.optionValues[0].swatch.color) {
              colorHex = opt.optionValues[0].swatch.color;
            }
          }
          break;
        }
      }
    }

    // images query uses reverse:true + first:1, so edges[0] is the last image
    const edges = node.images && node.images.edges;
    const img = edges && edges.length > 0 ? edges[0] : null;

    return {
      handle: node.handle,
      title: node.title,
      tags: node.tags || [],
      // Assumes 1 color value per sibling product (each product = 1 color in the family)
      featured_image: img ? { url: img.node.url } : null,
      variants: node.variants && node.variants.edges
        ? node.variants.edges.map((e) => ({ title: e.node.title }))
        : [],
      colorHex,
      colorName,
      available: node.availableForSale !== false,
    };
  }

  /**
   * @param {string} styleRef
   * @returns {Promise<Array|null>}
   */
  function fetchViaSfAPI(styleRef) {
    const query = `{ products(first: 24, query: "tag:${styleRef}-*") { edges { node { handle title tags availableForSale images(first: 1, reverse: true) { edges { node { url } } } options { name optionValues { name swatch { color } } } variants(first: 1) { edges { node { title } } } } } } }`;

    return fetch(`https://${window.STOREFRONT_DOMAIN}/api/2024-07/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.STOREFRONT_ACCESS_TOKEN,
      },
      body: JSON.stringify({ query }),
    })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((json) => {
        if (json.errors) throw new Error(json.errors[0].message);
        const edges = (json.data && json.data.products && json.data.products.edges) || [];
        return filterSiblings(
          edges.map((e) => normalizeFromStorefront(e.node)),
          styleRef
        );
      });
  }

  // ---------------------------------------------------------------------------
  // Predictive search fallback
  // ---------------------------------------------------------------------------

  /**
   * @param {string} styleRef
   * @returns {Promise<Array|null>}
   */
  function fetchViaPredictive(styleRef) {
    const params = [
      `q=${encodeURIComponent(styleRef)}`,
      'resources[type]=product',
      'resources[limit]=24',
      'resources[options][fields]=tag',
      'resources[options][unavailable_products]=show',
    ].join('&');

    return fetch(`/search/suggest.json?${params}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((json) => {
        const results =
          (json.resources &&
            json.resources.results &&
            json.resources.results.products) || [];
        return filterSiblings(results, styleRef);
      });
  }

  // ---------------------------------------------------------------------------
  // Main fetch — Storefront API with fallback to predictive search
  // ---------------------------------------------------------------------------

  /**
   * @param {string} styleRef
   * @returns {Promise<Array|null>}
   */
  function fetchSiblings(styleRef) {
    const cached = getFromCache(styleRef);
    if (cached) return Promise.resolve(cached);
    if (pendingRequests[styleRef]) return pendingRequests[styleRef];

    const primaryFetch = USE_STOREFRONT_API
      ? fetchViaSfAPI(styleRef)
      : fetchViaPredictive(styleRef);

    const promise = primaryFetch
      .catch(() => {
        if (USE_STOREFRONT_API) return fetchViaPredictive(styleRef);
        return null;
      })
      .then((products) => {
        if (products) saveToCache(styleRef, products);
        delete pendingRequests[styleRef];
        return products;
      })
      .catch(() => {
        delete pendingRequests[styleRef];
        return null;
      });

    pendingRequests[styleRef] = promise;
    return promise;
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /**
   * @param {Object} product
   * @returns {string}
   */
  function getColorName(product) {
    if (product.colorName) return product.colorName;
    if (product.variants && product.variants.length > 0) {
      const variantTitle = product.variants[0].title;
      if (variantTitle && variantTitle !== 'Default Title') {
        const parts = variantTitle.split(' / ');
        return parts[parts.length - 1];
      }
    }
    return product.title;
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  /**
   * @param {HTMLElement} container
   * @param {Array} siblings
   * @param {string} currentHandle
   */
  function renderSwatches(container, siblings, currentHandle) {
    // Remove skeletons
    container.querySelectorAll('.color-siblings__skeleton').forEach((el) => el.remove());
    container.querySelectorAll('.color-siblings__swatch[data-sibling="true"]').forEach((el) => el.remove());

    if (!siblings || siblings.length === 0) return;

    const swatchStyle = container.dataset.swatchStyle || 'image';
    const otherSiblings = siblings.filter(
      (s) => s.handle !== currentHandle && s.available !== false
    );

    if (otherSiblings.length === 0) return;

    const fragment = document.createDocumentFragment();

    otherSiblings.forEach((sibling) => {
      const colorName = getColorName(sibling);
      const link = document.createElement('a');
      link.href = '/products/' + sibling.handle;
      link.className = 'color-siblings__swatch';
      link.setAttribute('aria-label', colorName);
      link.setAttribute('title', colorName);
      link.dataset.handle = sibling.handle;
      link.dataset.sibling = 'true';

      if (swatchStyle === 'color') {
        const hex = sibling.colorHex || null;
        const dot = document.createElement('span');
        dot.className = hex
          ? 'color-siblings__swatch-dot'
          : 'color-siblings__swatch-fallback';
        if (hex) dot.style.backgroundColor = hex;
        link.appendChild(dot);
      } else {
        const imgObj = sibling.featured_image || sibling.image;
        const imgUrl = imgObj && imgObj.url;
        if (imgUrl) {
          const media = document.createElement('span');
          media.className = 'color-siblings__swatch-media';
          const img = document.createElement('img');
          img.src = imgUrl + (imgUrl.indexOf('?') !== -1 ? '&' : '?') + 'width=88';
          img.alt = colorName;
          img.width = 28;
          img.height = 28;
          img.loading = 'lazy';
          img.decoding = 'async';
          img.className = 'color-siblings__swatch-img';
          media.appendChild(img);
          link.appendChild(media);
        } else {
          const fallback = document.createElement('span');
          fallback.className = 'color-siblings__swatch-fallback';
          link.appendChild(fallback);
        }
      }

      fragment.appendChild(link);
    });

    container.appendChild(fragment);
  }

  // ---------------------------------------------------------------------------
  // PDP initialization
  // ---------------------------------------------------------------------------

  function initPDP() {
    const container = /** @type {HTMLElement | null} */ (
      document.querySelector('[data-style-ref][data-mode="pdp"]')
    );
    if (!container) return;

    const styleRef = container.dataset.styleRef;
    const currentHandle = container.dataset.currentHandle;
    if (!styleRef || !currentHandle) return;

    if (!container.querySelector('.color-siblings__skeleton')) {
      const frag = document.createDocumentFragment();
      for (let i = 0; i < 3; i++) {
        const skel = document.createElement('span');
        skel.className = 'color-siblings__swatch color-siblings__skeleton';
        skel.setAttribute('aria-hidden', 'true');
        const inner = document.createElement('span');
        inner.className = 'color-siblings__swatch-fallback color-siblings__swatch-fallback--skeleton';
        skel.appendChild(inner);
        frag.appendChild(skel);
      }
      container.appendChild(frag);
    }

    fetchSiblings(styleRef).then((siblings) => {
      if (!siblings) {
        container.querySelectorAll('.color-siblings__skeleton').forEach((el) => el.remove());
        return;
      }
      renderSwatches(container, siblings, currentHandle);
    });

    container.addEventListener('click', (e) => {
      const swatch = /** @type {HTMLElement | null} */ (
        /** @type {Element} */ (e.target).closest('.color-siblings__swatch')
      );
      if (!swatch) return;
      if (swatch.getAttribute('aria-current') === 'true') return;

      e.preventDefault();

      const handle = swatch.dataset.handle;
      if (!handle) return;

      container.classList.add('color-siblings--loading');
      window.location.href = '/products/' + handle;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPDP);
  } else {
    initPDP();
  }
})();
